import { isBefore, parseISO } from 'date-fns';
import type { EventModelConfig } from '@bryntum/scheduler-thin';
import type {
  OpenShiftState,
  OpenShiftNotAvailableReason,
} from '../../../components/Shifts/Common/types';
import {
  calculateOpenShiftState,
  getOpenShiftEventColor,
} from '../ColleaguesCalendar/openShiftUtils';
import type { SchedulerOpenShiftFieldsFragment } from '../../../__generated__/graphql';

type ShiftInterval = {
  start: Date;
  end: Date;
};

type MapOpenShiftsParams = {
  openShifts: readonly SchedulerOpenShiftFieldsFragment[];
  meId: string;
  myShiftIntervals: ShiftInterval[];
  targetNameMap: Record<string, string>;
  openShiftLabel: string;
};

export type OpenShiftEventData = EventModelConfig & {
  isOpenShift: true;
  openShiftId: string;
  openShiftState: OpenShiftState;
  openShiftNotAvailableReason?: OpenShiftNotAvailableReason;
  assignmentRequestId: string | null;
  targetName: string | null;
  breakPeriodMinutes: number;
};

export function mapOpenShiftsToEvents({
  openShifts,
  meId,
  myShiftIntervals,
  targetNameMap,
  openShiftLabel,
}: MapOpenShiftsParams): OpenShiftEventData[] {
  const now = new Date();

  return openShifts
    .filter((openShift) => {
      const shiftEnd = parseISO(openShift.flair__End_Datetime__c);
      if (isBefore(shiftEnd, now)) return false;
      const isConfirmed = openShift.Shifts.some(
        (s) => s.flair__Resource_Employee__c === meId,
      );
      return !isConfirmed;
    })
    .map((openShift) => {
      const { state, notAvailableReason } = calculateOpenShiftState(
        openShift,
        meId,
        myShiftIntervals,
      );
      const myRequest = openShift.AssignmentRequests.find(
        (r) =>
          r.flair__Employee__c === meId && r.flair__Confirmed_At__c === null,
      );

      const resolvedTargetName = openShift.flair__Linked_Target_Id__c
        ? targetNameMap[openShift.flair__Linked_Target_Id__c] ?? null
        : null;

      return {
        id: `open-shift-${openShift.Id}`,
        name: openShiftLabel,
        startDate: new Date(openShift.flair__Start_Datetime__c),
        endDate: new Date(openShift.flair__End_Datetime__c),
        eventColor: getOpenShiftEventColor(state),
        isOpenShift: true as const,
        openShiftId: openShift.Id,
        openShiftState: state,
        openShiftNotAvailableReason: notAvailableReason,
        assignmentRequestId: myRequest?.Id ?? null,
        targetName: resolvedTargetName,
        breakPeriodMinutes:
          openShift.flair__Total_Break_Period_in_Minutes__c || 0,
      };
    });
}
